import type { FaturaNF } from './nfStatus.js';
import { buildLayoutLines } from './pdfLayout.js';

declare global {
  interface Window {
    pdfjsLib?: {
      GlobalWorkerOptions?: { workerSrc?: string };
      getDocument: (src: { data: ArrayBuffer }) => {
        promise: Promise<{
          numPages: number;
          getPage: (pageNumber: number) => Promise<{
            getTextContent: () => Promise<{
              items: Array<{
                str?: string;
                width?: number;
                hasEOL?: boolean;
                transform?: number[];
              }>;
            }>;
          }>;
        }>;
      };
    };
  }
}

export type NFKind = 'comum' | 'bonificacao';

export interface ParsedNfPdf {
  numero: string | null;
  serie: string | null;
  chaveAcesso: string | null;
  emitente: string | null;
  cnpjEmitente: string | null;
  dataEmissao: string | null;
  naturezaOperacao: string | null;
  valorProdutos: number | null;
  valorIPI: number | null;
  valorICMS: number | null;
  valorTotal: number | null;
  pedidoNumero: string | null;
  tipoNF: NFKind;
  faturas: FaturaNF[];
}

export interface ExtractedNfPdfText {
  text: string;
  lines: string[];
  raw: string;
}

export function padPedido(value: string | null | undefined) {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits ? digits.replace(/^0+/, '').padStart(6, '0') : '';
}

export function normalizeSupplier(value: string | null | undefined) {
  return String(value ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');
}

export function parseMoneyBRL(value: string) {
  const normalized = value.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseDateBR(value: string) {
  const match = value.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (!match) return null;
  const [, d, m, y] = match;
  return `${y}-${m}-${d}`;
}

export async function extractNfPdfText(file: File): Promise<ExtractedNfPdfText> {
  if (!window.pdfjsLib) throw new Error('PDF.js não disponível');
  const buf = await file.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: buf }).promise;
  const coordPages: string[] = [];
  const rawPages: string[] = [];

  for (let i = 1; i <= pdf.numPages; i += 1) {
    const page = await pdf.getPage(i);
    const tc = await page.getTextContent();

    rawPages.push(tc.items.map(it => it.str ?? '').join(' '));

    const pageLines = buildLayoutLines(tc.items);
    if (!pageLines.length) continue;

    coordPages.push(pageLines.join('\n'));
  }

  const text = coordPages.join('\n\n').replace(/[ \t]+\n/g, '\n').trim();
  const lines = text.split(/\n+/).map(line => line.trim()).filter(Boolean);
  const raw = rawPages.join('\n').replace(/\s+/g, ' ').trim();
  return { text, lines, raw };
}

export function danfeTableValue(lines: string[], labelPattern: RegExp): string | null {
  for (let i = 0; i < lines.length - 1; i++) {
    const headerCols = lines[i].split('|').map(h => h.trim());
    const colIdx = headerCols.findIndex(h => labelPattern.test(h));
    if (colIdx < 0) continue;
    const dataCols = lines[i + 1].split('|').map(v => v.trim());
    return dataCols[colIdx] ?? null;
  }
  return null;
}

export function extractFaturasHorizontal(lines: string[]): FaturaNF[] {
  const fatIdx = lines.findIndex(l => /\bFATURA\b/i.test(l) && !/DADOS\s+ADI/i.test(l));
  if (fatIdx < 0) return [];
  const window = lines.slice(fatIdx + 1, fatIdx + 7);
  const dateLine = window.find(l => /\d{2}\/\d{2}\/\d{4}/.test(l));
  const valueLine = window.find(l => /[\d.]+,\d{2}/.test(l) && !/\d{2}\/\d{2}\/\d{4}/.test(l));
  if (!dateLine || !valueLine) return [];
  const dates = [...dateLine.matchAll(/(\d{2}\/\d{2}\/\d{4})/g)].map(m => parseDateBR(m[1]));
  const values = [...valueLine.matchAll(/([\d.]+,\d{2})/g)].map(m => parseMoneyBRL(m[1]));
  const count = Math.min(dates.length, values.length, 24);
  const faturas: FaturaNF[] = [];
  for (let i = 0; i < count; i++) {
    const vencimento = dates[i];
    const valor = values[i];
    if (!vencimento || valor == null || valor <= 0) continue;
    faturas.push({ n: i + 1, vencimento, valor });
  }
  return faturas;
}

export function parseNotaFiscalPDF(extracted: ExtractedNfPdfText): ParsedNfPdf {
  const { lines, raw } = extracted;
  const textFlat = extracted.text.replace(/\s*\|\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const rawFlat = raw.replace(/\s+/g, ' ').trim();

  const result: ParsedNfPdf = {
    numero: null, serie: null, chaveAcesso: null,
    emitente: null, cnpjEmitente: null,
    dataEmissao: null, naturezaOperacao: null,
    valorProdutos: null, valorIPI: null, valorICMS: null, valorTotal: null,
    pedidoNumero: null, tipoNF: 'comum', faturas: [],
  };

  const lineLeft = (line: string) => line.split('|')[0].trim();

  let m = rawFlat.match(/N[º.°]\s*0*(\d{4,9})\s+S[ÉE]RIE/i)
    ?? rawFlat.match(/NF-e\s+N[º.°]\s*0*(\d{4,9})/i)
    ?? rawFlat.match(/N[º.°]\s*0*(\d{4,9})/i)
    ?? textFlat.match(/N[º.°]\s*0*(\d{4,9})/i)
    ?? textFlat.match(/\bN[FOE]{0,2}[-\s]*e?\s*(?:n[ºo°]\s*)?(\d{5,12})\b/i);
  if (m) result.numero = m[1];

  m = textFlat.match(/S[ÉE]RIE\s*[:\-]?\s*([A-Z0-9]{1,6})\b/i);
  if (m) result.serie = m[1];

  m = textFlat.match(/((?:\d\s*){44})/);
  if (m) result.chaveAcesso = m[1].replace(/\s+/g, '');

  // Fonte primária: linha do canhoto "RECEBEMOS DE <razão social> OS PRODUTOS CONSTANTES..."
  // Traz o nome completo sem a quebra de linha da caixa do emitente.
  m = rawFlat.match(/RECEBEMOS\s+DE\s+(.+?)\s+OS\s+PRODUTOS\s+CONSTANTES/i)
    ?? textFlat.match(/RECEBEMOS\s+DE\s+(.+?)\s+OS\s+PRODUTOS\s+CONSTANTES/i);
  if (m) {
    const cand = m[1].replace(/\s*\|\s*/g, ' ').replace(/\s+/g, ' ').trim();
    if (cand && !/NF-?e/i.test(cand)) result.emitente = cand;
  }

  const emitIdx = lines.findIndex(l => /Identifica[cç][aã]o\s+do\s+emitente/i.test(l));
  if (!result.emitente && emitIdx >= 0) {
    const parts: string[] = [];
    for (let i = emitIdx + 1; i <= Math.min(emitIdx + 4, lines.length - 1); i++) {
      const left = lineLeft(lines[i]);
      if (!left || /^(RODOVIA|ROD\s|RUA\s|R\.\s|AV[.\s]|AVENIDA|EST\s|ESTRADA|ENDERE[CÇ]|CNPJ|INSCRI|CHAVE\s+DE\s+ACESSO|FONE|CEP\b)/i.test(left)) break;
      if (/DOCUMENTO\s+AUXILIAR|NOTA\s+FISCAL\s+ELETR|DANFE/i.test(left)) continue;
      parts.push(left);
    }
    if (parts.length) result.emitente = parts.join(' ').replace(/\s+/g, ' ').trim();
  }
  if (!result.emitente) {
    m = rawFlat.match(/Raz[ãa]o\s+Social\s*:\s*(.+?)\s+(?:C[óo]digo|CNPJ|Loja)\s*[:.\-]/i)
      ?? rawFlat.match(/(?:RAZ[ÃA]O\s+SOCIAL|NOME\s*\/\s*RAZ[ÃA]O\s+SOCIAL)\s*[:\-]?\s*(.+?)\s+(?:CNPJ|IE|INSCRI[ÇC][ÃA]O)/i);
    if (m) result.emitente = m[1].replace(/\s*\|\s*/g, ' ').trim();
  }

  const cnpjs = [...textFlat.matchAll(/\b(\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})\b/g)].map(x => x[1]);
  if (cnpjs.length) result.cnpjEmitente = cnpjs[0];

  const natVal = danfeTableValue(lines, /NATUREZA\s+DA\s+OPERA[CÇ][AÃ]O/i);
  if (natVal) result.naturezaOperacao = natVal.split('|')[0].trim();
  if (!result.naturezaOperacao) {
    m = rawFlat.match(/NATUREZA\s+DA\s+OPERA[CÇ][AÃ]O\s+(.+?)(?:\s+PROTOCOLO|\s+INSCRI)/i);
    if (m) result.naturezaOperacao = m[1].trim();
  }

  const dataEmVal = danfeTableValue(lines, /DATA\s+DE\s+EMISS[ÃA]O/i);
  if (dataEmVal) {
    const dtsInVal = dataEmVal.match(/(\d{2}\/\d{2}\/\d{4})/);
    if (dtsInVal) result.dataEmissao = parseDateBR(dtsInVal[1]);
  }
  if (!result.dataEmissao) {
    m = rawFlat.match(/DATA\s+DE\s+EMISS[ÃA]O\s+(\d{2}\/\d{2}\/\d{4})/i)
      ?? textFlat.match(/DATA\s+DE\s+EMISS[ÃA]O\s+(\d{2}\/\d{2}\/\d{4})/i);
    if (m) result.dataEmissao = parseDateBR(m[1]);
  }

  const vtNota = danfeTableValue(lines, /VALOR\s+TOTAL\s+DA\s+NOTA/i);
  if (vtNota) result.valorTotal = parseMoneyBRL(vtNota);
  const vtProd = danfeTableValue(lines, /VALOR\s+TOTAL\s+DOS\s+PRODUTOS/i);
  if (vtProd) result.valorProdutos = parseMoneyBRL(vtProd);
  const vtIPI = danfeTableValue(lines, /^VALOR\s+DO\s+IPI$/i);
  if (vtIPI) result.valorIPI = parseMoneyBRL(vtIPI);
  const vtICMS = danfeTableValue(lines, /^VALOR\s+DO\s+ICMS$/i);
  if (vtICMS) result.valorICMS = parseMoneyBRL(vtICMS);

  if (result.valorTotal == null) {
    m = rawFlat.match(/VALOR\s+TOTAL\s+DA\s+NOTA\s+([\d.]+,\d{2})/i);
    if (m) result.valorTotal = parseMoneyBRL(m[1]);
  }
  if (result.valorProdutos == null) {
    m = rawFlat.match(/VALOR\s+TOTAL\s+DOS\s+PRODUTOS\s+([\d.]+,\d{2})/i);
    if (m) result.valorProdutos = parseMoneyBRL(m[1]);
  }
  if (result.valorIPI == null) {
    m = rawFlat.match(/VALOR\s+DO\s+IPI\s+([\d.]+,\d{2})/i);
    if (m) result.valorIPI = parseMoneyBRL(m[1]);
  }
  if (result.valorICMS == null) {
    m = rawFlat.match(/VALOR\s+DO\s+ICMS\s+([\d.]+,\d{2})/i);
    if (m) result.valorICMS = parseMoneyBRL(m[1]);
  }
  if (result.valorTotal == null) {
    const totals = [...textFlat.matchAll(/([\d]{1,3}(?:\.\d{3})*,\d{2})/g)]
      .map(x => parseMoneyBRL(x[1])).filter((v): v is number => v != null);
    if (totals.length) result.valorTotal = Math.max(...totals);
  }

  m = rawFlat.match(/Pedido\s+de\s+Compra:?\s*(\d{4,8})/i)
    ?? textFlat.match(/Pedido\s+de\s+Compra:?\s*(\d{4,8})/i)
    ?? textFlat.match(/PEDIDO(?:\s+DE\s+COMPRA)?\s*[:\-]?\s*(\d{4,12})/i);
  if (m) result.pedidoNumero = m[1].replace(/^0+/, '').padStart(6, '0');

  const fatRegex = /\b0*(\d{1,3})\s+(\d{2}\/\d{2}\/\d{4})\s+([\d.]+,\d{2})/g;
  const fatBlock = rawFlat.match(/FATURA([\s\S]*?)(?:CALCULO|CALCULADO|TRANSPORTADOR|DADOS\s+ADICIONAIS|VALOR\s+TOTAL)/i);
  if (fatBlock) {
    const seenFat = new Set<string>();
    [...fatBlock[1].matchAll(fatRegex)].forEach(fm => {
      const n = parseInt(fm[1], 10);
      const vencimento = parseDateBR(fm[2]);
      const valor = parseMoneyBRL(fm[3]);
      if (!vencimento || valor == null || n < 1 || n > 24 || valor <= 0) return;
      const key = `${vencimento}__${valor.toFixed(2)}`;
      if (seenFat.has(key)) return;
      seenFat.add(key);
      result.faturas.push({ n, vencimento, valor });
    });
  }
  if (!result.faturas.length) result.faturas = extractFaturasHorizontal(lines);
  if (!result.faturas.length) {
    const seenFat = new Set<string>();
    let fallbackN = 1;
    [...lines.join('\n').matchAll(/(\d{2}\/\d{2}\/\d{4}).{0,30}?([\d.]+,\d{2})/g)].forEach(match => {
      const vencimento = parseDateBR(match[1]);
      const valor = parseMoneyBRL(match[2]);
      if (!vencimento || valor == null) return;
      const key = `${vencimento}__${valor.toFixed(2)}`;
      if (seenFat.has(key)) return;
      seenFat.add(key);
      result.faturas.push({ n: fallbackN++, vencimento, valor });
    });
    result.faturas = result.faturas.slice(0, 24);
  }

  const haystack = ((result.naturezaOperacao ?? '') + ' ' + textFlat).toUpperCase();
  const cfopBonif = /\bCFOP\s*[:.\-]?\s*(5910|6910|5911|6911|5912|6912)\b/.test(textFlat)
    || /\b(5910|6910)\b/.test(textFlat.replace(/[^\d\s]/g, ' '));
  const txtBonif = /BONIFICA[CÇ][AÃ]O|REMESSA\s+EM\s+BONIFICA|BRINDE|DOA[CÇ][AÃ]O|AMOSTRA\s+GR[AÁ]TIS/.test(haystack);
  if (cfopBonif || txtBonif) result.tipoNF = 'bonificacao';

  return result;
}
